import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { api, type SettingItem } from '@/api/client';
import { ErrorBox, Field, Loading, PageTitle, fullW } from '@/components/ui';

type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

interface ClanEmblem {
  id: string;
  name: string;
  imageUrl: string | null;
  rarity: Rarity;
  priceGems: number;
  special: boolean;
  awardRank: number | null;
  isActive: boolean;
}

interface ClanBossFieldSpec {
  key: string;
  label: string;
  unit?: string;
  scale?: number;
}

const CLAN_BOSS_GROUPS: Array<{ title: string; fields: ClanBossFieldSpec[] }> = [
  {
    title: 'Battle tuning',
    fields: [
      { key: 'clan.boss.baseHp', label: 'Level 1 base HP', unit: 'HP' },
      { key: 'clan.boss.hpGrowth', label: 'HP growth per level', unit: '×' },
      { key: 'clan.boss.durationMs', label: 'Attack duration', unit: 'seconds', scale: 1000 },
      { key: 'clan.boss.maxLevel', label: 'Maximum weekly level' },
      { key: 'clan.boss.eliteEvery', label: 'Elite boss every', unit: 'levels' },
      { key: 'clan.boss.minPerfectHits', label: 'Perfect hits for reward' },
      { key: 'clan.boss.activeMemberDays', label: 'Active-member window', unit: 'days' },
      { key: 'clan.boss.lowHpNotifyPercent', label: 'Low HP notification', unit: '%' },
      { key: 'clan.boss.tzOffsetMinutes', label: 'Daily reset UTC offset', unit: 'minutes' },
    ],
  },
  {
    title: 'Rewards',
    fields: [
      { key: 'clan.boss.rewardBaseGems', label: 'Base gems per contributor' },
      { key: 'clan.boss.rewardIncreaseEvery', label: 'Increase reward every', unit: 'levels' },
      { key: 'clan.boss.rewardIncreaseGems', label: 'Gems added per interval' },
      { key: 'clan.boss.eliteBonusGems', label: 'Elite bonus gems' },
      { key: 'clan.boss.weeklyGemCap', label: 'Weekly gem cap per player' },
      { key: 'clan.boss.clanXpPerLevel', label: 'Clan XP per defeated level' },
    ],
  },
];

async function req<T>(method: string, path: string, body?: unknown, isForm = false): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  if (!isForm) headers['Content-Type'] = 'application/json';
  const res = await fetch(path, {
    method, headers,
    body: isForm ? (body as FormData) : body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let d = '';
    try { const j = await res.json(); d = j?.message ?? j?.error ?? ''; } catch { /* */ }
    throw new Error(d || `HTTP ${res.status}`);
  }
  return res.json();
}

export function ClanEmblemsPage() {
  const [items, setItems] = useState<ClanEmblem[] | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [bossSettings, setBossSettings] = useState<SettingItem[]>([]);
  const [bossEdits, setBossEdits] = useState<Record<string, unknown>>({});
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [savingFeature, setSavingFeature] = useState(false);
  const [savingBoss, setSavingBoss] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const [emblemsResponse, settingsResponse] = await Promise.all([
        req<{ items: ClanEmblem[] }>('GET', '/admin/clan-emblems/'),
        api.settings(),
      ]);
      setItems(emblemsResponse.items);
      setEnabled(
        Boolean(
          settingsResponse.items.find(
            (setting: SettingItem) => setting.key === 'clan.enabled',
          )?.value,
        ),
      );
      setBossSettings(
        settingsResponse.items.filter((setting) => setting.category === 'clan.boss'),
      );
      setErr(null);
    } catch (error) {
      setErr(String(error));
    }
  };

  useEffect(() => { void load(); }, []);

  if (err && !items) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  async function toggleFeature(): Promise<void> {
    setSavingFeature(true);
    setErr(null);
    try {
      await api.setSetting('clan.enabled', !enabled);
      await load();
    } catch (error) {
      setErr(String(error));
    } finally {
      setSavingFeature(false);
    }
  }

  async function createNew(special: boolean) {
    setCreating(true);
    try {
      await req('POST', '/admin/clan-emblems/', {
        name: special ? 'New Season Logo' : 'New Clan Logo',
        rarity: special ? 'legendary' : 'epic',
        special,
        awardRank: special ? 1 : null,
      });
      await load();
    } catch (e: any) { alert(e?.message); }
    setCreating(false);
  }

  async function saveBossSettings(): Promise<void> {
    const dirtySettings = bossSettings.filter((setting) => setting.key in bossEdits);
    if (dirtySettings.length === 0) return;
    setSavingBoss(true);
    setErr(null);
    try {
      // Persist the enable switch last so a newly enabled mode never starts
      // with a partially updated HP/reward configuration.
      const ordered = [
        ...dirtySettings.filter((setting) => setting.key !== 'clan.boss.enabled'),
        ...dirtySettings.filter((setting) => setting.key === 'clan.boss.enabled'),
      ];
      for (const setting of ordered) {
        await api.setSetting(setting.key, bossEdits[setting.key]);
      }
      setBossEdits({});
      await load();
    } catch (error) {
      setErr(String(error));
    } finally {
      setSavingBoss(false);
    }
  }

  function resetBossSettings(): void {
    setBossEdits(
      Object.fromEntries(bossSettings.map((setting) => [setting.key, setting.defaultValue])),
    );
  }

  const buyable = items.filter((i) => !i.special);
  const awards = items.filter((i) => i.special);

  return (
    <div>
      <PageTitle title="Clan" />
      {err && <ErrorBox text={err} />}

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <div>
            <div className="display" style={{ fontSize: 18 }}>
              MASTER FEATURE FLAG
            </div>
            <div style={{ color: 'var(--ink-muted)', fontSize: 12, marginTop: 4 }}>
              {enabled
                ? 'Clan is visible. API, chat, and Clan War are active.'
                : 'Clan is hidden. Data is preserved while all Clan actions are blocked.'}
            </div>
          </div>
          <button
            className={`btn ${enabled ? 'btn-danger' : 'btn-primary'}`}
            disabled={savingFeature}
            onClick={() => void toggleFeature()}
          >
            {savingFeature
              ? 'SAVING…'
              : enabled
                ? 'DISABLE CLAN'
                : 'ENABLE CLAN'}
          </button>
        </div>
      </div>

      {bossSettings.length > 0 && (
        <ClanBossSettingsPanel
          items={bossSettings}
          edits={bossEdits}
          saving={savingBoss}
          onChange={(key, value) =>
            setBossEdits((current) => ({ ...current, [key]: value }))
          }
          onSave={() => void saveBossSettings()}
          onReset={resetBossSettings}
        />
      )}

      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--ink-muted)', background: 'rgba(155,93,229,0.08)', borderColor: 'rgba(155,93,229,0.25)' }}>
        Premium clan emblems. Upload a square PNG/WebP logo (auto-optimized to 256px WebP).
        <b> Buyable</b> logos are bought with gems &amp; owned by the clan forever.
        <b> Season-reward</b> logos (set <b>award rank 1/2/3</b>) are granted to the Top 1/2/3 clans
        each weekly season and revoked at the next reset — not for sale.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-primary" onClick={() => createNew(false)} disabled={creating}>+ BUYABLE LOGO</button>
        <button className="btn" onClick={() => createNew(true)} disabled={creating}>+ SEASON REWARD</button>
      </div>

      <div className="tag" style={{ marginBottom: 8, color: 'var(--c-purple)' }}>BUYABLE (gems)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {buyable.map((s) => <EmblemRow key={s.id} item={s} onChanged={load} />)}
        {buyable.length === 0 && <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>No buyable logos yet.</div>}
      </div>

      <div className="tag" style={{ marginBottom: 8, color: 'var(--c-yellow)' }}>SEASON REWARDS (Top 1/2/3)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {awards.map((s) => <EmblemRow key={s.id} item={s} onChanged={load} />)}
        {awards.length === 0 && <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>No season-reward logos yet.</div>}
      </div>
    </div>
  );
}

function ClanBossSettingsPanel({
  items,
  edits,
  saving,
  onChange,
  onSave,
  onReset,
}: {
  items: SettingItem[];
  edits: Record<string, unknown>;
  saving: boolean;
  onChange: (key: string, value: unknown) => void;
  onSave: () => void;
  onReset: () => void;
}) {
  const itemMap = new Map(items.map((item) => [item.key, item]));
  const value = (key: string): unknown =>
    key in edits ? edits[key] : itemMap.get(key)?.value;
  const enabled = Boolean(value('clan.boss.enabled'));
  const dirty = items.some((item) => item.key in edits);
  const baseHp = Number(value('clan.boss.baseHp') ?? 0);
  const growth = Number(value('clan.boss.hpGrowth') ?? 1);
  const maxLevel = Number(value('clan.boss.maxLevel') ?? 1);
  const finalLevelHp = Math.min(
    2_000_000_000,
    Math.round(baseHp * Math.pow(growth, Math.max(0, maxLevel - 1))),
  );

  return (
    <div
      className="card"
      style={{
        padding: 16,
        marginBottom: 16,
        borderColor: enabled ? 'rgba(244,199,36,0.45)' : 'rgba(230,57,70,0.4)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 14,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 18, color: 'var(--c-yellow)' }}>
            CLAN BOSS CONFIG
          </div>
          <div style={{ color: 'var(--ink-muted)', fontSize: 12, marginTop: 4 }}>
            One attack per member each day. Changes affect newly started attacks; current attacks keep their snapshot.
          </div>
        </div>
        <select
          value={String(enabled)}
          onChange={(event) => onChange('clan.boss.enabled', event.target.value === 'true')}
          style={{ width: 190 }}
        >
          <option value="true">Clan Boss enabled</option>
          <option value="false">Clan Boss disabled</option>
        </select>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 330px), 1fr))',
          gap: 14,
        }}
      >
        {CLAN_BOSS_GROUPS.map((group) => (
          <section
            key={group.title}
            style={{
              padding: 14,
              border: '1px solid var(--hairline)',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.018)',
            }}
          >
            <div className="tag" style={{ color: 'var(--c-cyan)', marginBottom: 12 }}>
              {group.title.toUpperCase()}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 145px), 1fr))',
                gap: 10,
              }}
            >
              {group.fields.map((field) => {
                const item = itemMap.get(field.key);
                if (!item) return null;
                const scale = field.scale ?? 1;
                const rawValue = Number(value(field.key) ?? 0);
                const displayValue = rawValue / scale;
                const min = item.min === undefined ? undefined : item.min / scale;
                const max = item.max === undefined ? undefined : item.max / scale;
                return (
                  <Field
                    key={field.key}
                    label={field.label}
                    hint={`${field.unit ? `${field.unit} · ` : ''}range ${min ?? '—'}–${max ?? '—'}`}
                  >
                    <input
                      type="number"
                      value={displayValue}
                      min={min}
                      max={max}
                      step={item.type === 'integer' && scale === 1 ? 1 : 0.1}
                      onChange={(event) => {
                        const parsed = Number(event.target.value);
                        if (!Number.isFinite(parsed)) return;
                        const stored = parsed * scale;
                        onChange(
                          field.key,
                          item.type === 'integer' ? Math.round(stored) : Math.round(stored * 100) / 100,
                        );
                      }}
                      style={fullW}
                    />
                  </Field>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div
        style={{
          marginTop: 12,
          padding: '9px 11px',
          borderRadius: 8,
          background: 'rgba(244,199,36,0.08)',
          color: 'var(--ink-muted)',
          fontSize: 12,
        }}
      >
        Preview for one active member: level 1 = {baseHp.toLocaleString('en-US')} HP · level {maxLevel} ≈{' '}
        {finalLevelHp.toLocaleString('en-US')} HP. Larger clans are scaled sub-linearly by active members.
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <button className="btn" onClick={onReset} disabled={saving}>
          LOAD DEFAULTS
        </button>
        <button
          className="btn btn-primary"
          onClick={onSave}
          disabled={!dirty || saving}
          style={{ opacity: dirty ? 1 : 0.45 }}
        >
          {saving ? 'SAVING…' : 'SAVE CLAN BOSS'}
        </button>
      </div>
    </div>
  );
}

function EmblemRow({ item, onChanged }: { item: ClanEmblem; onChanged: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(item.name);
  const [rarity, setRarity] = useState<Rarity>(item.rarity);
  const [priceGems, setPriceGems] = useState(item.priceGems);
  const [special, setSpecial] = useState(item.special);
  const [awardRank, setAwardRank] = useState<number>(item.awardRank ?? 1);
  const [isActive, setIsActive] = useState(item.isActive);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await req('PATCH', `/admin/clan-emblems/${item.id}`, {
        name, rarity, priceGems, special,
        awardRank: special ? awardRank : null,
        isActive,
      });
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function del() {
    if (!confirm(`Delete "${item.name}"? Clans who own it lose it.`)) return;
    setBusy(true);
    try { await req('DELETE', `/admin/clan-emblems/${item.id}`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function upload(f: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      await req('POST', `/admin/clan-emblems/${item.id}/image`, fd, true);
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  return (
    <div className="card" style={{ padding: 14, display: 'flex', gap: 14, flexWrap: 'wrap', opacity: isActive ? 1 : 0.6 }}>
      <div style={{ width: 56, height: 56, background: 'rgba(0,0,0,0.3)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {item.imageUrl
          ? <img src={item.imageUrl} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} />
          : <span style={{ fontSize: 9, color: 'var(--ink-dim)' }}>no img</span>}
      </div>

      <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <div style={{ display: 'flex', gap: 6 }}>
          <select value={rarity} onChange={(e) => setRarity(e.target.value as Rarity)} style={{ flex: 1 }}>
            {['common', 'rare', 'epic', 'legendary'].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {!special && (
            <input type="number" value={priceGems} onChange={(e) => setPriceGems(+e.target.value)} placeholder="gems" style={{ width: 90 }} />
          )}
          {special && (
            <select value={awardRank} onChange={(e) => setAwardRank(+e.target.value)} style={{ width: 110 }}>
              <option value={1}>Top 1</option>
              <option value={2}>Top 2</option>
              <option value={3}>Top 3</option>
            </select>
          )}
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
          <label><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> active</label>
          <label><input type="checkbox" checked={special} onChange={(e) => setSpecial(e.target.checked)} /> season reward</label>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, justifyContent: 'center' }}>
        <input type="file" accept="image/png,image/webp,image/gif" ref={fileRef} style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy}>{item.imageUrl ? 'REPLACE IMG' : 'UPLOAD IMG'}</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>SAVE</button>
        <button className="btn" onClick={del} disabled={busy} style={{ color: 'var(--c-red)' }}>DELETE</button>
      </div>
    </div>
  );
}
